import React, { useState } from "react";
import { FileText, Plus, X, TicketPercent, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import QuotationDetailsModal from "./QuotationDetailsModal";
import { ICommissionType, IEpcQuotation } from "../../models/epcCustomer.model";
import {
  activateQuote,
  addRepaymentTask,
  confirmQuote,
  getAllEpcQuotes,
  openQuote,
  updateOtherInverter,
  updateQuoteDiscount,
} from "../../store/slices/epcSlice";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import ToggleSwitch from "../Users/ToggleSwitch";
import { getUser } from "../../utils/auth";

interface QuotationsListProps {
  quotations?: IEpcQuotation[];
  isMainModalOpen?: boolean;
  setIsMainModalOpen?: (isOpen: boolean) => void;
  setActiveTab?: (tab: "overview" | "kyc" | "payments" | "quotations") => void;
}

const QuotationsList: React.FC<QuotationsListProps> = ({
  quotations,
  setActiveTab,
  setIsMainModalOpen,
}) => {
  const isQuoteOpening = useSelector(
    (state: RootState) => state.epc.loadingState.openingQuoteLoading,
  );
  const isAddingRepayment = useSelector(
    (state: RootState) => state.epc.loadingState.addingRepaymentLoading,
  );
  const dispatch: RootDispatch = useDispatch();
  const role = useSelector((state: RootState) => state.auth.role);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [localActiveId, setLocalActiveId] = useState<string | null>(null);
  const [selectedQuotation, setSelectedQuotation] =
    useState<IEpcQuotation | null>(null);
  const [quotationToConfirm, setQuotationToConfirm] =
    useState<IEpcQuotation | null>(null);
  const [discountInputs, setDiscountInputs] = useState<{
    [key: string]: { show: boolean; value: string };
  }>({});
  const [showRepayment, setShowRepayment] = useState<{
    [key: string]: boolean;
  }>({});
  const [repaymentAmounts, setRepaymentAmounts] = useState<{
    [key: string]: number;
  }>({});
  const [customCommission, setCustomCommission] = useState<number | "">("");
  const [selectedOption, setSelectedOption] = useState<
    "customCommission" | "excessAmount" | null
  >(null);
  const [excessAmount, setExcessAmount] = useState<number | "">("");
  const [commissionType, setCommissionType] = useState<ICommissionType>(
    ICommissionType.per_order,
  );
  const [otherInverter, setOtherInverter] = useState<boolean>(false);
  const currentUser =
    useSelector((state: RootState) => state.auth.currentUser) || getUser();

  const handleWheelPrevent = usePreventWheelChange();

  const handleOpenQuotationData = (quotation: IEpcQuotation) => {
    setSelectedQuotation(quotation);
    setIsDetailsModalOpen(true);
  };

  const handleOpenConfirmModal = (quotation: IEpcQuotation) => {
    setQuotationToConfirm(quotation);
    setIsConfirmModalOpen(true);
    setOtherInverter(false);
  };

  const resetConfirmModalState = () => {
    setIsConfirmModalOpen(false);
    setQuotationToConfirm(null);
    setSelectedOption(null);
    setCustomCommission("");
    setExcessAmount("");
    setCommissionType(ICommissionType.per_kw);
    setOtherInverter(false);
  };

  const handleConfirmQuote = async () => {
    if (!quotationToConfirm) return;

    if (selectedOption === "customCommission") {
      if (customCommission === "" || !commissionType) {
        toast.error("Please enter commission amount and type");
        return;
      }
    }

    if (selectedOption === "excessAmount") {
      if (excessAmount === "") {
        toast.error("Please enter excess amount");
        return;
      }
    }

    try {
      if (otherInverter) {
        await dispatch(
          updateOtherInverter({
            quoteId: quotationToConfirm._id,
            otherInverter: true,
          }),
        ).unwrap();
      }

      await dispatch(
        confirmQuote({
          quoteId: quotationToConfirm._id,
          customCommission:
            selectedOption === "customCommission"
              ? customCommission === ""
                ? undefined
                : customCommission
              : undefined,
          customCommissionType:
            selectedOption === "customCommission" ? commissionType : undefined,
          excessAmount:
            selectedOption === "excessAmount"
              ? excessAmount === ""
                ? undefined
                : excessAmount
              : undefined,
        }),
      ).unwrap();

      await dispatch(getAllEpcQuotes());
      setIsConfirmModalOpen(false);
      setQuotationToConfirm(null);
      setIsMainModalOpen?.(false);
      setCustomCommission("");
      setExcessAmount("");
      setSelectedOption(null);
      setOtherInverter(false);

      setActiveTab?.("overview");
    } catch (err: any) {
      toast.error("Error confirming quote");
    }
  };

  const handleAddRepayment = async (quotation: IEpcQuotation) => {
    try {
      await dispatch(
        addRepaymentTask({
          mobileNumber: quotation.mobileNumber,
          customerName: quotation.customerName,
          totalCost: quotation.totalCost,
          quotationId: quotation._id,
        }),
      ).unwrap();

      toast.success("Repayment task created successfully!");

      setShowRepayment((prev) => ({
        ...prev,
        [quotation._id]: false,
      }));
    } catch (err: any) {
      toast.error(err || "Failed to create repayment");
      console.error(err);
    }
  };

  const handleOpenQuotation = async (quotation: IEpcQuotation) => {
    try {
      await dispatch(openQuote(quotation?._id)).unwrap();
      toast.success("Opening quotation PDF...");
    } catch (err: any) {
      toast.error(err || "Failed to open quotation PDF");
    }
  };

  const handleShowDiscountInput = (quotationId: string) => {
    setDiscountInputs((prev) => ({
      ...prev,
      [quotationId]: { show: true, value: "" },
    }));
  };

  const handleHideDiscountInput = (quotationId: string) => {
    setDiscountInputs((prev) => ({
      ...prev,
      [quotationId]: { show: false, value: "" },
    }));
  };

  const handleDiscountChange = (quotationId: string, value: string) => {
    setDiscountInputs((prev) => ({
      ...prev,
      [quotationId]: { ...prev[quotationId], value },
    }));
  };

  const handleUpdateDiscount = async (quotation: IEpcQuotation) => {
    const discountValue = discountInputs[quotation._id]?.value;

    if (
      !discountValue ||
      isNaN(Number(discountValue)) ||
      Number(discountValue) < 0
    ) {
      toast.error("Please enter a valid discount amount");
      return;
    }

    const discountAmount = Number(discountValue);

    try {
      await dispatch(
        updateQuoteDiscount({
          quoteId: quotation._id,
          discount: discountAmount,
        }),
      ).unwrap();

      handleHideDiscountInput(quotation._id);
    } catch (err: any) {
      console.error("Error updating discount:", err);
    }
  };

  const handleActivateUI = async (quotationId: string) => {
    const result = await dispatch(
      activateQuote({
        quoteId: quotationId,
      }),
    ).unwrap();

    if (result?.success) {
      setLocalActiveId(quotationId);
    }
  };

  if (!quotations || quotations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
          <FileText className="w-8 h-8 text-neutral-400" />
        </div>
        <h3 className="text-lg font-medium text-neutral-900 mb-2">
          No Quotations Found
        </h3>
        <p className="text-sm text-neutral-500">
          No quotations have been created yet.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        <div className="grid gap-3">
          {quotations
            .filter((quotation) => {
              if (role === "LE") return quotation.isConfirmed === true;
              return true;
            })
            .map((quotation, index) => (
              <motion.div
                key={quotation._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all duration-200 hover:border-blue-300"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className={`w-3 h-3 rounded-full flex-shrink-0 ${
                        (
                          localActiveId
                            ? localActiveId === quotation._id
                            : quotation?.isActive
                        )
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-normal gap-2">
                        <span className="text-[10px] font-semibold text-neutral-500 truncate">
                          {quotation?.quotationNumber}
                        </span>
                      </div>
                      <div className="flex items-center justify-normal gap-2">
                        <h3 className="text-base font-semibold text-neutral-900 truncate">
                          {quotation?.customerName}
                        </h3>
                      </div>

                      <div className="flex items-center gap-2 text-xs text-neutral-500 mt-0.5">
                        {quotation?.plantSize ? (
                          <span className="font-bold">
                            {quotation?.plantSize?.replace(/kw/i, "").trim()} KW
                          </span>
                        ) : null}
                        <span>•</span>
                        <span className="font-medium">{quotation?.brand}</span>
                        <span>•</span>
                        <span>{quotation?.panelType}</span>
                        <span>•</span>
                        <span>
                          {quotation?.otherInverter
                            ? "Other Inverter"
                            : "Frelit Inverter"}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 mt-2">
                        <div>
                          <span className="text-xs">ChatBot Status:</span>
                          <span
                            className={`inline-flex items-center px-1 py-1 rounded-full text-xs font-medium ${quotation?.isConfirmed ? " text-green-700" : ""}`}
                          >
                            {quotation?.isConfirmed ? "Confirmed" : "Pending"}
                          </span>
                        </div>

                        <div>
                          <span className="text-xs">Dashboard Status:</span>
                          <span
                            className={`inline-flex items-center px-1 py-1 rounded-full text-xs font-medium ${quotation?.isConfirmedFromWeb ? " text-green-700" : ""}`}
                          >
                            {quotation?.isConfirmedFromWeb
                              ? "Confirmed"
                              : "Pending"}
                          </span>
                        </div>

                        {showRepayment[quotation._id] && (
                          <div className="ml-4">
                            <div className="flex items-center gap-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded p-2">
                              <span>Repayment Amount: ₹</span>
                              <input
                                type="number"
                                className="w-24 px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                                value={
                                  repaymentAmounts[quotation._id] ??
                                  quotation.totalCost
                                }
                                onWheel={handleWheelPrevent}
                                onChange={(e) =>
                                  setRepaymentAmounts((prev) => ({
                                    ...prev,
                                    [quotation._id]: Number(e.target.value),
                                  }))
                                }
                                min={0}
                              />
                            </div>
                            <div className="mt-2">
                              <button
                                onClick={() =>
                                  handleAddRepayment({
                                    ...quotation,
                                    totalCost:
                                      repaymentAmounts[quotation._id] ??
                                      quotation.totalCost,
                                  })
                                }
                                disabled={isAddingRepayment}
                                className={`inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 ${isAddingRepayment ? "opacity-50 cursor-not-allowed" : ""}`}
                              >
                                <CreditCard className="w-3 h-3 mr-1" />
                                {isAddingRepayment
                                  ? "Processing..."
                                  : "Create Repayment"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      disabled={isQuoteOpening}
                      onClick={() => handleOpenQuotation(quotation)}
                      className={`inline-flex items-center px-2.5 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${isQuoteOpening ? "opacity-50 cursor-not-allowed" : ""}`}
                      title="Open Quotation"
                    >
                      {isQuoteOpening ? "Opening..." : "Open PDF"}
                    </button>

                    <button
                      onClick={() => handleOpenQuotationData(quotation)}
                      className="inline-flex items-center px-2.5 py-1.5 bg-gray-600 text-white text-xs font-medium rounded hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                      title="Open Quotation Data"
                    >
                      Open Data
                    </button>

                    {!quotation.isConfirmed &&
                      role !== "LE" &&
                      currentUser?.role !== "account" &&
                      currentUser?.role !== "dispatch-lv1" &&
                      currentUser?.role !== "dispatch-lv2" &&
                      (discountInputs[quotation._id]?.show ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            className="px-2 py-1 border border-gray-300 rounded text-xs w-20"
                            placeholder="Discount"
                            value={discountInputs[quotation._id].value}
                            onWheel={handleWheelPrevent}
                            onChange={(e) =>
                              handleDiscountChange(
                                quotation._id,
                                e.target.value,
                              )
                            }
                            min={0}
                          />
                          <button
                            onClick={() => handleUpdateDiscount(quotation)}
                            className="inline-flex items-center px-2 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add
                          </button>
                          <button
                            onClick={() =>
                              handleHideDiscountInput(quotation._id)
                            }
                            className="inline-flex items-center px-2 py-1 bg-gray-500 text-white text-xs font-medium rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        currentUser?.role !== "Commission Dealer" &&
                        currentUser?.role !== "Channel Partner" && (
                          <button
                            onClick={() =>
                              handleShowDiscountInput(quotation._id)
                            }
                            className="inline-flex items-center px-2.5 py-1.5 bg-yellow-500 text-white text-xs font-medium rounded hover:bg-yellow-600 transition-colors"
                          >
                            <TicketPercent className="w-3 h-3 mr-1" /> Discount
                          </button>
                        )
                      ))}

                    {!quotation.isConfirmedFromWeb &&
                      role !== "LE" &&
                      role !== "account" &&
                      role !== "dispatch-lv1" &&
                      role !== "dispatch-lv2" && (
                        <button
                          onClick={() => handleOpenConfirmModal(quotation)}
                          className="inline-flex items-center px-2.5 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                          title="Confirm Quotation"
                        >
                          Confirm
                        </button>
                      )}

                    {!(localActiveId
                      ? localActiveId === quotation._id
                      : quotation?.isActive) &&
                      (role === "admin" || role === "bdm") && (
                        <button
                          onClick={() => handleActivateUI(quotation._id)}
                          className="inline-flex items-center px-2.5 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded hover:bg-indigo-700 transition-colors"
                          title="Activate"
                        >
                          Activate
                        </button>
                      )}
                  </div>
                </div>
              </motion.div>
            ))}
        </div>
      </div>

      <QuotationDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedQuotation(null);
        }}
        quotation={selectedQuotation!}
      />

      <AnimatePresence>
        {isConfirmModalOpen && quotationToConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsConfirmModalOpen(false);
                setQuotationToConfirm(null);
              }}
              className="absolute inset-0 bg-black bg-opacity-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    Confirm Quotation
                  </h3>
                  <p className="text-sm text-neutral-600 mb-4">
                    Are you sure you want to confirm this quotation for{" "}
                    <span className="font-semibold">
                      {quotationToConfirm.customerName}
                    </span>
                    ? This action cannot be undone.
                  </p>

                  <div className="bg-gray-50 rounded p-3 mb-4">
                    <div className="text-xs text-neutral-500 space-y-1">
                      <div>
                        <span className="font-medium">Brand:</span>{" "}
                        {quotationToConfirm.brand}
                      </div>
                      <div>
                        <span className="font-medium">Panel Type:</span>{" "}
                        {quotationToConfirm.panelType}
                      </div>
                      <div>
                        <span className="font-medium">Total Cost:</span> ₹
                        {quotationToConfirm.totalCost?.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 p-3 bg-purple-50 rounded border border-purple-100">
                    <ToggleSwitch
                      id={`other-inverter-${quotationToConfirm._id}`}
                      checked={otherInverter}
                      onChange={(checked) => setOtherInverter(checked)}
                      label="Other Inverter"
                      showIcon={false}
                      containerClassName="flex items-center justify-between p-0 bg-transparent border-0"
                      sizeVariant="xs"
                    />
                  </div>

                  {currentUser.role !== "Channel Partner" &&
                    currentUser.role !== "Commission Dealer" && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Select Adjustment Type{" "}
                          <span className="text-gray-500">(Optional)</span>
                        </label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedOption("customCommission")
                            }
                            className={`flex-1 px-4 py-2 text-sm font-medium rounded border transition-colors ${selectedOption === "customCommission" ? "bg-blue-100 border-blue-500 text-blue-700" : "border-gray-300 text-gray-700 hover:bg-gray-200"}`}
                          >
                            Deduction
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedOption("excessAmount")}
                            className={`flex-1 px-4 py-2 text-sm font-medium rounded border transition-colors ${selectedOption === "excessAmount" ? "bg-blue-100 border-blue-500 text-blue-700" : "border-gray-300 text-gray-700 hover:bg-gray-200"}`}
                          >
                            Excess Amount
                          </button>
                        </div>
                      </div>
                    )}

                  {selectedOption === "customCommission" && (
                    <div className="space-y-4 mb-4 p-4 bg-blue-50 rounded border border-blue-100">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">
                          Commission Type
                        </label>
                        <select
                          value={commissionType}
                          onChange={(e) =>
                            setCommissionType(e.target.value as ICommissionType)
                          }
                          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          <option value={ICommissionType.per_order}>
                            Per Order
                          </option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-1">
                          Deduction Amount (₹)
                        </label>
                        <input
                          type="number"
                          value={customCommission}
                          onChange={(e) =>
                            setCustomCommission(
                              e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                            )
                          }
                          onWheel={handleWheelPrevent}
                          min={0}
                          max={quotationToConfirm.totalCost}
                          placeholder="Enter deduction amount"
                          className="w-full px-3 py-2 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        />
                        <p className="text-xs text-blue-600 mt-1">
                          This amount will be deducted from the total commission
                        </p>
                      </div>
                    </div>
                  )}

                  {selectedOption === "excessAmount" && (
                    <div className="mb-4 p-4 bg-green-50 rounded border border-green-100">
                      <label className="block text-sm font-medium text-green-700 mb-1">
                        Excess Amount (₹)
                      </label>
                      <input
                        type="number"
                        value={excessAmount}
                        onChange={(e) =>
                          setExcessAmount(
                            e.target.value === "" ? "" : Number(e.target.value),
                          )
                        }
                        onWheel={handleWheelPrevent}
                        min={0}
                        placeholder="Enter excess amount"
                        className="w-full px-3 py-2 border border-green-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                      />
                      <p className="text-xs text-red-600 mt-1">
                        8.9% GST will be deducted from the excess amount
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-3 justify-end">
                    <button
                      onClick={resetConfirmModalState}
                      className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
                    >
                      No, Cancel
                    </button>
                    <button
                      onClick={handleConfirmQuote}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                    >
                      Yes, Confirm
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default QuotationsList;
